import { z } from 'zod'

/**
 * ChartSpec — the contract the AI emits inside a ```chart fenced block.
 * The model only describes WHAT to draw (symbol, type, timeframe, overlays);
 * the client fetches the actual OHLC series from the EODHD proxy and renders it
 * with lightweight-charts. This keeps token usage tiny and the data always live.
 */
export const TimeframeSchema = z.enum(['1m', '5m', '15m', '1h', '1D', '1W', '1M'])
export type Timeframe = z.infer<typeof TimeframeSchema>

export const ChartTypeSchema = z.enum(['candlestick', 'line', 'area'])
export type ChartType = z.infer<typeof ChartTypeSchema>

export const OverlaySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('sma'),
    period: z.number().int().positive().max(400),
    color: z.string().optional(),
    id: z.string().optional(),
  }),
  z.object({
    kind: z.literal('ema'),
    period: z.number().int().positive().max(400),
    color: z.string().optional(),
    id: z.string().optional(),
  }),
  z.object({
    kind: z.literal('hline'),
    price: z.number(),
    label: z.string().optional(),
    color: z.string().optional(),
    style: z.enum(['solid', 'dashed', 'dotted']).optional(),
  }),
  z.object({
    kind: z.literal('marker'),
    time: z.string(),
    text: z.string().optional(),
    position: z.enum(['above', 'below']).optional(),
    color: z.string().optional(),
    shape: z.enum(['circle', 'arrowUp', 'arrowDown', 'square']).optional(),
  }),
])
export type Overlay = z.infer<typeof OverlaySchema>

export const PaneSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('volume') }),
  z.object({
    kind: z.literal('rsi'),
    period: z.number().int().positive().default(14),
    bands: z.tuple([z.number(), z.number()]).default([30, 70]),
  }),
  z.object({
    kind: z.literal('macd'),
    fast: z.number().int().positive().default(12),
    slow: z.number().int().positive().default(26),
    signal: z.number().int().positive().default(9),
  }),
])
export type Pane = z.infer<typeof PaneSchema>

export const ChartSpecSchema = z.object({
  version: z.literal('1').default('1'),
  symbol: z.string().min(1),
  title: z.string().optional(),
  type: ChartTypeSchema.default('candlestick'),
  timeframe: TimeframeSchema.default('1D'),
  range: z
    .object({ from: z.string().optional(), to: z.string().optional() })
    .optional(),
  overlays: z.array(OverlaySchema).max(8).optional(),
  panes: z.array(PaneSchema).max(3).optional(),
})
export type ChartSpec = z.infer<typeof ChartSpecSchema>

/** Tolerant parse: returns null instead of throwing, for use while streaming. */
export function safeParseChartSpec(raw: unknown): ChartSpec | null {
  const result = ChartSpecSchema.safeParse(raw)
  return result.success ? result.data : null
}
